/**
 * Type for teacher-feedback course metadata documents stored in Firestore at:
 * teacherFeedback/data/courses/{courseId}
 */
import {z} from 'zod';

// ============================================================================
// Document Schema
// ============================================================================

/**
 * Schema for course-scoped teacher-feedback metadata.
 *
 * `zoomConnectorUserId` is the Firebase Auth UID of the admin whose Zoom OAuth
 * tokens are currently used for course-level Zoom API operations.
 *
 * `isActive`: when omitted or false, {@link isTeacherFeedbackCourseActive}
 * treats the course as inactive (no webhook/transcript automation for that course).
 */
export const TeacherFeedbackCourseSchema = z.object({
  /** UID of the admin user who connected this course to Zoom. */
  zoomConnectorUserId: z.string().optional(),
  /**
   * Whether this course is active for teacher-feedback automation. Missing or
   * false means inactive.
   */
  isActive: z.boolean().optional(),
});

/** Course metadata for a specific course in the teacher-feedback app. */
export type TeacherFeedbackCourse = z.infer<typeof TeacherFeedbackCourseSchema>;

/**
 * Returns whether the course should be treated as active. Documents without
 * `isActive` (or with `isActive: false`) are inactive.
 */
export function isTeacherFeedbackCourseActive(
  course: Pick<TeacherFeedbackCourse, 'isActive'> | null | undefined,
): boolean {
  return course?.isActive === true;
}

// ============================================================================
// Paths
// ============================================================================

/**
 * Returns the path to the teacher-feedback courses collection.
 * Path: teacherFeedback/data/courses
 */
export function getTeacherFeedbackCoursesCollectionPath(): string {
  return 'teacherFeedback/data/courses';
}

/**
 * Returns the path to a course metadata document.
 * Path: teacherFeedback/data/courses/{courseId}
 */
export function getTeacherFeedbackCourseDocumentPath({
  courseId,
}: {
  courseId: string;
}): string {
  return `${getTeacherFeedbackCoursesCollectionPath()}/${courseId}`;
}
